import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { PageHeader } from "@/components/ui/PageHeader"
import { Textarea } from "@/components/ui/textarea"

export const Route = createFileRoute("/design/textarea-showcase")({
  component: TextareaShowcase,
})

function TextareaShowcase() {
  const [values, setValues] = useState({
    default: "",
    withLabel: "",
    withHelper: "",
    withError: "",
    disabled: "",
    required: "",
    noResize: "",
    horizontalResize: "",
  })

  return (
    <div className="bg-background min-h-screen p-8">
      <div className="mx-auto max-w-4xl">
        <PageHeader
          title="Textarea Component"
          description="A multi-line text input component following the Shelfi design system"
        />

        <div className="mt-8 space-y-12">
          {/* Basic Textareas */}
          <section>
            <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 mb-6 text-2xl font-semibold">
              Basic Textarea States
            </h2>
            <div className="space-y-6">
              {/* Default Textarea */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Default Textarea</h3>
                <Textarea
                  placeholder="Enter your message..."
                  value={values.default}
                  onChange={(e) => setValues({ ...values, default: e.target.value })}
                />
              </div>

              {/* Textarea with Label */}
              <div>
                <h3 className="mb-3 text-lg font-medium">With Label</h3>
                <Textarea
                  label="Description"
                  placeholder="Provide a detailed description..."
                  value={values.withLabel}
                  onChange={(e) => setValues({ ...values, withLabel: e.target.value })}
                />
              </div>

              {/* Textarea with Helper Text */}
              <div>
                <h3 className="mb-3 text-lg font-medium">With Helper Text</h3>
                <Textarea
                  label="Bio"
                  helperText="Write a short bio about yourself. Maximum 500 characters."
                  placeholder="Tell us about yourself..."
                  value={values.withHelper}
                  onChange={(e) => setValues({ ...values, withHelper: e.target.value })}
                />
              </div>

              {/* Textarea with Error */}
              <div>
                <h3 className="mb-3 text-lg font-medium">With Error State</h3>
                <Textarea
                  label="Comments"
                  error="Comments must be at least 10 characters long"
                  placeholder="Add your comments..."
                  value={values.withError}
                  onChange={(e) => setValues({ ...values, withError: e.target.value })}
                />
              </div>

              {/* Required Textarea */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Required Field</h3>
                <Textarea
                  label="Feedback"
                  required
                  placeholder="Your feedback is important to us..."
                  value={values.required}
                  onChange={(e) => setValues({ ...values, required: e.target.value })}
                />
              </div>

              {/* Disabled Textarea */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Disabled State</h3>
                <Textarea
                  label="Notes"
                  disabled
                  placeholder="This field is disabled"
                  value="This textarea is disabled and cannot be edited"
                />
              </div>
            </div>
          </section>

          {/* Resize Options */}
          <section>
            <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 mb-6 text-2xl font-semibold">
              Resize Behavior
            </h2>
            <div className="space-y-6">
              {/* No Resize */}
              <div>
                <h3 className="mb-3 text-lg font-medium">No Resize</h3>
                <Textarea
                  label="Fixed Size"
                  resize="none"
                  placeholder="This textarea cannot be resized..."
                  value={values.noResize}
                  onChange={(e) => setValues({ ...values, noResize: e.target.value })}
                />
              </div>

              {/* Horizontal Resize */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Horizontal Resize Only</h3>
                <Textarea
                  label="Horizontal Resize"
                  resize="horizontal"
                  placeholder="This textarea can only be resized horizontally..."
                  value={values.horizontalResize}
                  onChange={(e) => setValues({ ...values, horizontalResize: e.target.value })}
                />
              </div>

              {/* Both Directions */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Resize Both Directions</h3>
                <Textarea
                  label="Full Resize"
                  resize="both"
                  placeholder="This textarea can be resized in both directions..."
                  helperText="Drag the corner to resize in any direction"
                />
              </div>
            </div>
          </section>

          {/* Backward Compatibility */}
          <section>
            <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 mb-6 text-2xl font-semibold">
              Backward Compatibility Mode
            </h2>
            <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">
              When used without the new props (label, helperText, error), the Textarea component
              maintains backward compatibility with the old implementation.
            </p>
            <div className="space-y-6">
              <div>
                <h3 className="mb-3 text-lg font-medium">Simple Textarea (Old API)</h3>
                <Textarea placeholder="This uses the old API" className="max-w-md" />
              </div>
            </div>
          </section>

          {/* Design Specifications */}
          <section className="rounded-lg bg-neutral-50 p-6 dark:bg-neutral-900">
            <h2 className="mb-4 text-xl font-semibold">Design Specifications</h2>
            <div className="grid gap-6 text-sm md:grid-cols-2">
              <div>
                <h3 className="mb-2 font-medium">Dimensions</h3>
                <ul className="space-y-1 text-neutral-600 dark:text-neutral-400">
                  <li>• Min height: 92px (~3 lines)</li>
                  <li>• Horizontal padding: 24px</li>
                  <li>• Vertical padding: 12px</li>
                  <li>• Border width: 1px (2px on focus)</li>
                  <li>• Default resize: Vertical only</li>
                </ul>
              </div>
              <div>
                <h3 className="mb-2 font-medium">States</h3>
                <ul className="space-y-1 text-neutral-600 dark:text-neutral-400">
                  <li>• Default: Neutral border</li>
                  <li>• Hover: Darker border</li>
                  <li>• Focus: 2px blue border</li>
                  <li>• Error: Red border with triangle icon</li>
                  <li>• Disabled: Gray background</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
